import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, BookOpen, Star } from "lucide-react";

import { cmsApi } from "@profitlens/api-client";
import { LanguageSwitcher } from "@/components/language-switcher";
import { ThemeToggle } from "@/components/theme-toggle";
import { Button } from "@profitlens/ui/button";
import { Spinner } from "@profitlens/ui/spinner";
import { useSession } from "@/features/auth/use-session";
import { normalizeLanguage } from "@/i18n";

/**
 * 根路由：官网首页。
 *
 * 文案 / 特性 / 定价卡片 / FAQ / 帮助文档都来自管理后台「官网管理」（/cms/** 公开接口，
 * 免登录）。接口不可用时回退到内置 i18n 文案，保证首屏永远不是空白页。
 */
export function LandingPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useSession();
  const locale = normalizeLanguage(i18n.resolvedLanguage ?? i18n.language);

  const landing = useQuery({
    queryKey: ["cms", "landing", locale],
    queryFn: () => cmsApi.landing(locale),
  });
  const faq = useQuery({
    queryKey: ["cms", "faq", locale],
    queryFn: () => cmsApi.faq(locale),
  });
  const docs = useQuery({
    queryKey: ["cms", "help-docs", locale],
    queryFn: () => cmsApi.helpDocs(locale),
  });

  const content = landing.data;
  const heroTitle = content?.hero_title || t("app.tagline");
  const heroSubtitle = content?.hero_subtitle || t("app.description");
  const primaryCta = content?.primary_cta_label || t("auth.goRegister");
  const secondaryCta = content?.secondary_cta_label || t("auth.loginSubmit");
  const features = content?.features ?? [];
  const pricingCards = content?.pricing_cards ?? [];
  const faqItems = faq.data ?? [];
  const helpDocs = docs.data ?? [];

  useEffect(() => {
    if (content?.seo_title) document.title = content.seo_title;
  }, [content?.seo_title]);

  return (
    <div className="flex min-h-dvh flex-col bg-background">
      <header className="flex items-center justify-between px-4 py-3 sm:px-6">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand text-sm font-bold text-brand-fg">
            PL
          </span>
          <span className="text-sm font-semibold">{t("app.name")}</span>
        </div>
        <div className="flex items-center gap-2">
          {helpDocs.length > 0 ? (
            <Button variant="ghost" size="sm" asChild>
              <Link to="/help">{t("landing.helpTitle")}</Link>
            </Button>
          ) : null}
          <LanguageSwitcher compact />
          <ThemeToggle compact />
        </div>
      </header>

      {/* Hero */}
      <main className="flex-1">
        <section className="px-4 py-16 sm:px-6 sm:py-24">
          <div className="mx-auto max-w-3xl space-y-6 text-center">
            {landing.isLoading ? (
              <Spinner className="mx-auto h-5 w-5" />
            ) : (
              <>
                <h1 className="text-3xl font-semibold tracking-tight sm:text-5xl">{heroTitle}</h1>
                <p className="text-base text-muted sm:text-lg">{heroSubtitle}</p>
                <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
                  {isAuthenticated ? (
                    <Button size="lg" onClick={() => navigate("/app/dashboard")}>
                      {t("nav.dashboard")}
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  ) : (
                    <>
                      <Button size="lg" asChild>
                        <Link to="/register">
                          {primaryCta}
                          <ArrowRight className="h-4 w-4" />
                        </Link>
                      </Button>
                      <Button size="lg" variant="outline" asChild>
                        <Link to="/login">{secondaryCta}</Link>
                      </Button>
                    </>
                  )}
                </div>
                <p className="text-xs text-muted">{t("auth.demoHint")}</p>
              </>
            )}
          </div>
        </section>

        {/* 特性 */}
        {features.length > 0 ? (
          <section className="border-t border-border bg-surface-muted/40 px-4 py-14 sm:px-6">
            <div className="mx-auto grid max-w-5xl gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {features.map((feature) => (
                <div key={feature.title} className="rounded-xl border border-border bg-surface p-5">
                  <p className="text-sm font-semibold">{feature.title}</p>
                  <p className="mt-1.5 text-sm text-muted">{feature.description}</p>
                </div>
              ))}
            </div>
          </section>
        ) : null}

        {/* 定价 */}
        {pricingCards.length > 0 ? (
          <section className="px-4 py-14 sm:px-6">
            <div className="mx-auto max-w-5xl">
              <div className="mx-auto max-w-2xl space-y-2 text-center">
                <h2 className="text-2xl font-semibold tracking-tight">
                  {content?.pricing_title || t("landing.pricingFallbackTitle")}
                </h2>
                {content?.pricing_subtitle ? (
                  <p className="text-sm text-muted">{content.pricing_subtitle}</p>
                ) : null}
              </div>

              <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {pricingCards.map((card) => (
                  <div
                    key={card.code}
                    className={
                      card.is_highlighted
                        ? "relative rounded-xl border-2 border-brand bg-surface p-5 shadow-sm"
                        : "relative rounded-xl border border-border bg-surface p-5"
                    }
                  >
                    {card.is_highlighted ? (
                      <span className="absolute -top-2.5 right-4 inline-flex items-center gap-1 rounded-full bg-brand px-2 py-0.5 text-[11px] font-medium text-brand-fg">
                        <Star className="h-3 w-3" />
                        {t("landing.recommended")}
                      </span>
                    ) : null}
                    <p className="text-sm font-semibold">{card.display_name || card.code}</p>
                    <p className="mt-3 text-2xl font-semibold tracking-tight">
                      {card.amount}
                      <span className="ml-1 text-xs font-normal text-muted">{card.currency}</span>
                    </p>
                    <p className="text-xs text-muted">
                      {t("landing.perDays", { count: card.days })}
                    </p>
                    {card.card_description ? (
                      <p className="mt-3 text-sm text-muted">{card.card_description}</p>
                    ) : null}
                    {card.card_features.length > 0 ? (
                      <ul className="mt-4 space-y-1.5">
                        {card.card_features.map((item) => (
                          <li key={item} className="flex items-start gap-2 text-sm text-muted">
                            <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-brand" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          </section>
        ) : null}

        {/* FAQ */}
        {faqItems.length > 0 ? (
          <section className="border-t border-border px-4 py-14 sm:px-6">
            <div className="mx-auto max-w-3xl">
              <h2 className="text-2xl font-semibold tracking-tight">{t("landing.faqTitle")}</h2>
              <div className="mt-6 divide-y divide-border rounded-xl border border-border bg-surface">
                {faqItems.map((item) => (
                  <details key={item.id} className="group px-4 py-3">
                    <summary className="cursor-pointer list-none text-sm font-medium marker:hidden">
                      {item.question}
                    </summary>
                    <p className="mt-2 text-sm whitespace-pre-wrap text-muted">{item.answer}</p>
                  </details>
                ))}
              </div>
            </div>
          </section>
        ) : null}

        {/* 帮助文档入口 */}
        {helpDocs.length > 0 ? (
          <section className="border-t border-border bg-surface-muted/40 px-4 py-14 sm:px-6">
            <div className="mx-auto max-w-3xl">
              <h2 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
                <BookOpen className="h-5 w-5 text-brand" />
                {t("landing.helpTitle")}
              </h2>
              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                {helpDocs.slice(0, 4).map((doc) => (
                  <Link
                    key={doc.id}
                    to={`/help/${doc.slug}`}
                    className="rounded-xl border border-border bg-surface p-4 transition-colors hover:border-brand"
                  >
                    <p className="text-sm font-semibold">{doc.title}</p>
                    {doc.summary ? (
                      <p className="mt-1.5 line-clamp-2 text-sm text-muted">{doc.summary}</p>
                    ) : null}
                  </Link>
                ))}
              </div>
            </div>
          </section>
        ) : null}
      </main>

      <footer className="border-t border-border px-4 py-6 text-center text-xs text-muted sm:px-6">
        {content?.footer_note || t("app.name")}
      </footer>
    </div>
  );
}
